import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCollaboratorCallEnded } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import type { EmergencyCallsState } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';

type RootState = {
  emergencyCalls: EmergencyCallsState;
};

const CALL_DATA_EVENT_TYPE = 'call_data.event';
const CALL_ENDED_EVENT_TYPE = 'call_ended';

/**
 * Hook that handles call_ended websocket events for collaborator calls.
 * Updates the collaborator.callEnded status in Redux when a call ends.
 *
 * Note: Endpoint-loaded collaborator data is handled in getRTTCollaboratorData.fulfilled.
 */
const useHandleCollaboratorCallEnded = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch = useDispatch();
  const callQueue = useSelector(
    (state: RootState) => state.emergencyCalls.callQueue,
  );

  const handleCallEndedEvent = useCallback(
    (msg: any) => {
      if (
        msg?.type !== CALL_DATA_EVENT_TYPE ||
        msg?.body?.event?.event_type !== CALL_ENDED_EVENT_TYPE ||
        msg?.body?.event?.event_name !== 'call_status' ||
        !msg?.topic
      ) {
        return;
      }

      const parts = `${msg.topic}`.split('tel://');
      if (parts.length > 1) {
        const callerId = parts[1];
        const call = callQueue[callerId];

        // Only mark collaborator calls as ended
        if (call?.collaborator?.type) {
          dispatch(
            setCollaboratorCallEnded({ callerID: callerId, callEnded: true }),
          );
        }
      }
    },
    [dispatch, callQueue],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleCollaboratorCallEnded: handleCallEndedEvent,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleCollaboratorCallEnded');
      };
    }
  }, [StreamWS, handleCallEndedEvent]);

  return null;
};

export default useHandleCollaboratorCallEnded;
